/**
 * The field-state tint wrapper and its legend (product owner, 5 Oct).
 *
 * `@norican/react`'s `Field` takes no state hook for a tint, so the field is
 * wrapped in a plain div carrying token-based classes (see
 * `lib/field-highlight.ts`). The state is also exposed as `data-highlight`
 * for tests and styling, and is never the only signal: required fields keep
 * their asterisk, prefilled ones their "filled in for you" notes.
 */

import { Typography } from "@norican/react";
import type { FocusEvent, ReactNode } from "react";

import { useT } from "@/i18n";
import {
  FIELD_HIGHLIGHT_CLASSES,
  type FieldHighlight,
  fieldHighlightClass,
} from "@/lib/field-highlight";

const SWATCH = "inline-block size-3 rounded-sm border-s-4";

export interface HighlightedFieldProps {
  state: FieldHighlight;
  /**
   * Names the field for a summary's "Edit" (`data-field-key`), which brings
   * it back into view (`revealField`).
   */
  fieldKey?: string;
  /**
   * Called once focus leaves the whole field (not when it moves between the
   * parts of one control, such as a date's day, month and year).
   */
  onLeave?: () => void;
  children: ReactNode;
}

/** One field, tinted Accent Blue (filled in for you) or Norican Yellow (still needed). */
export function HighlightedField({ state, fieldKey, onLeave, children }: HighlightedFieldProps) {
  const handleBlur =
    onLeave === undefined
      ? undefined
      : (event: FocusEvent<HTMLDivElement>) => {
          const next = event.relatedTarget;
          if (next instanceof Node && event.currentTarget.contains(next)) {
            return;
          }
          onLeave();
        };
  return (
    <div
      className={fieldHighlightClass(state)}
      data-highlight={state ?? "none"}
      data-field-key={fieldKey}
      onBlur={handleBlur}
    >
      {children}
    </div>
  );
}

/** The one-line legend at the top of a form. */
export function FieldHighlightLegend() {
  const { t } = useT("common");
  return (
    <Typography variant="caption" tone="muted" className="flex flex-wrap items-center gap-x-3">
      <span className="flex items-center gap-1.5">
        <span aria-hidden className={`${SWATCH} ${FIELD_HIGHLIGHT_CLASSES.prefilled}`} />
        {t("fieldHighlight.prefilled")}
      </span>
      <span aria-hidden>·</span>
      <span className="flex items-center gap-1.5">
        <span aria-hidden className={`${SWATCH} ${FIELD_HIGHLIGHT_CLASSES.required}`} />
        {t("fieldHighlight.required")}
      </span>
    </Typography>
  );
}
